import { DuplicateButton } from '@/components/admin/duplicate-button';
import { TooltipShield } from '@/components/admin/tooltip-shield';
import type { CriterionDraft } from '@/lib/criterion-draft';
import {
  addBand,
  bandUpperFromInput,
  canAddBand,
  canAddExample,
  canRemoveBand,
  passableChoices,
  removeBand,
  withPassable,
  withPlancher,
} from '@/lib/criterion-draft';
import { bandProblemSentence, scaleProblemSentence } from '@/lib/criterion-labels';
import { scalePreview } from '@/lib/criterion-scale';
import { exampleDuplicationBlocked } from '@/lib/duplication';
import { EMPTY_SELECT_VALUE, fromSelectValue, toSelectValue } from '@/lib/select-value';
import type { CriterionScoringDraft } from '@matter_extractor/shared';
import {
  CRITERION_BAND_TEXT_MAX,
  CRITERION_DIRECTION_MAX,
  CRITERION_EXAMPLES_MAX,
  CRITERION_EXAMPLE_MAX,
  CRITERION_NAME_MAX,
  CRITERION_SCORING_BANDS_MAX,
  CRITERION_SCORING_INSTRUCTION_MAX,
  CRITERION_SCORING_TOP,
  bandLowerBounds,
  bandProblem,
  emptyScoring,
  scaleProblem,
  scoringProblems,
} from '@matter_extractor/shared';
import { Icon, Select, TextArea, TextField, Tooltip } from '@pms-digital/design-system';
import { type ReactElement, useId, useState } from 'react';

/**
 * The left column of the editor: a name, a direction, one list of positive
 * examples (refonte modulaire §6; maquette `Editeur.dc.html`).
 *
 * That is the whole of a criterion - no counter-examples, no labels on the
 * examples, no weight - and the shape of a version's content is exactly
 * `{ name, direction, examples }`.
 *
 * ## Every field is the SAME WIDTH, and that costs a gutter
 *
 * The maquette fixes it, and it is the one layout rule of this column: an
 * example carries a remove cross and the two other fields do not, so a naive
 * layout would make the examples narrower than the direction above them and the
 * column would read as two columns. The cross therefore lives in a 36 px
 * GUTTER outside every field, and the name and the direction reserve the same
 * gutter with an `aria-hidden` spacer. Written as a spacer rather than as
 * `padding-right` on the parent because only the fields align on it - the
 * « Ajouter un exemple » row and the counters align on it too.
 *
 * ## The example's number is INSIDE its field
 *
 * §6, from the maquette: « un exemple est un champ pleine largeur avec son
 * numéro intégré à gauche ». A number outside the box would be a third column;
 * inside, on a `gray-50` cell against the field's own border, it reads as part
 * of the field and the text still starts at a predictable x.
 *
 * ## `readOnly` is the « Voir » of the history (§4), and it is not `disabled`
 *
 * A version opened read-only renders in the same shape rather than in a dialog
 * of its own: comparing two versions in this lot is done by eye (« pas de
 * diff », §6), and an eye compares two things that look alike.
 *
 * Which is exactly why the fields carry the `readOnly` ATTRIBUTE and not
 * `disabled`. A disabled control leaves the tab order and, in Chrome, its text
 * stops being selectable - so the one state of this screen whose whole purpose
 * is reading would be the one where a four-thousand-character direction cannot
 * be scrolled with the keyboard, selected or copied into a message. `readOnly`
 * refuses the edit and keeps the reading. The crosses and « Ajouter un
 * exemple » are removed rather than greyed, because a gesture that is closed
 * for the whole screen is not a control at all here.
 *
 * The plancher radios are the ONE exception, and it is the browser's: `readOnly`
 * does nothing to a radio - it would still change on a click - so they carry
 * `disabled`, which costs nothing here because a radio holds no text to select.
 *
 * ## « Notation », and the two things the section does not decide
 *
 * The scale's arithmetic is not here. Whether a draft would parse as a version
 * is `scoringProblems` in `shared`, the same walk the server refuses on; where
 * each band lands on the preview rail is `criterion-scale.ts`, because jsdom has
 * no layout and a percentage computed in a component is a number nothing can
 * test. What is left in this file is what they LOOK like: a yellow border and a
 * caption on the faulty band, a hatched stretch where the rail has no bounds.
 */

export interface CriterionEditorFieldsProps {
  draft: CriterionDraft;
  /** A version opened by « Voir », or a retired criterion: nothing is typable. */
  readOnly: boolean;
  /** The rename refusal, worded by the caller from the server's `code`. */
  nameError: string | null;
  onName: (next: string) => void;
  /**
   * The name is committed on BLUR and not on every keystroke, because a rename
   * is its own write (`PATCH`) and not part of the draft autosave: §4 has it
   * update the entity AND the draft without creating a version. Firing per
   * character would ask the server to check unicity against every prefix of
   * what somebody is typing.
   */
  onNameCommit: () => void;
  onDirection: (next: string) => void;
  onExample: (index: number, next: string) => void;
  onAddExample: () => void;
  onRemoveExample: (index: number) => void;
  /**
   * « Dupliquer » - the copy lands JUST AFTER the example at `index` (§4.2).
   *
   * A fourth callback beside the three the examples already take, and not a
   * variant of `onAddExample`: an addition appends a blank field, a duplication
   * inserts a value in the middle, and the list is POSITIONAL.
   */
  onDuplicateExample: (index: number) => void;
  /**
   * The whole scoring, or `null` for « pas de notation » (§6).
   *
   * ONE callback where the examples take three, and it is still « one per
   * field »: `scoring` is one field of the content, and its gestures - adding a
   * band, moving the plancher, removing the scale - are edits of that one
   * value. The edits themselves are pure functions of `criterion-draft.ts`, so
   * what crosses here is always a complete next scoring.
   */
  onScoring: (next: CriterionScoringDraft | null) => void;
}

/**
 * The WHOLE gutter, reserved so every field of this editor ends at the same x.
 *
 * 80 px since #117, and the arithmetic is worth writing down: the gutter now
 * holds TWO controls of 36 px - the copy icon of §4.2, then the remove cross -
 * separated by the row's own `gap-2`. Sized for one control, it would leave the
 * examples 44 px narrower than the name, the direction and the notation, and
 * §6 asks the notation to sit « sous les exemples, à la même largeur et avec la
 * même gouttière ».
 */
function Gutter() {
  return <span aria-hidden="true" className="block w-20 shrink-0" />;
}

/**
 * ONE slot of that gutter: the 36 px a single control occupies.
 *
 * Used where a row carries a cross but no copy icon - a scoring band, which
 * §4.2 lists under « ce qui ne se duplique pas » because « sa borne haute est
 * strictement croissante, donc une copie serait invalide à la seconde où elle
 * existe ». The empty slot is that absence, held open so the cross stays under
 * the crosses above it.
 */
function GutterSlot() {
  return <span aria-hidden="true" className="block w-9 shrink-0" />;
}

/**
 * The bubble that says why a band's cross is closed, hung only while it is: the
 * package `Tooltip` has no « no text, stay shut » of its own, and an open bubble
 * with nothing to say would be a white box under every live cross.
 */
function BandRemoveReason({ closed, children }: { closed: boolean; children: ReactElement }) {
  return closed ? (
    <Tooltip content="Une notation garde au moins deux tranches.">{children}</Tooltip>
  ) : (
    children
  );
}

/** The quiet link of the section's foot, and the row it shares with nothing. */
const QUIET_LINK =
  'cursor-pointer rounded-sm text-gray-600 text-small underline underline-offset-2 hover:text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit';

interface ScoringFieldsProps {
  scoring: CriterionScoringDraft;
  readOnly: boolean;
  onScoring: (next: CriterionScoringDraft | null) => void;
}

/**
 * The scoring once it exists: the instruction, the preview, the bands, and the
 * two gestures at the foot (§6, maquette `Main.dc.html`).
 *
 * Split from the form above it because the closed state is one button and this
 * is a hundred lines of rows - and because everything here reads ONE value of
 * the draft, which is exactly what `onScoring` sends back whole.
 */
function ScoringFields({ scoring, readOnly, onScoring }: ScoringFieldsProps) {
  const bandsHintId = useId();
  const captionId = useId();
  // From a `useId` and never a literal: §13's guided form may mount this whole
  // form twice on one page, and two literal group names would be ONE native
  // radio group - the two scales fighting over a single checked band.
  const plancherGroup = useId();
  const [confirming, setConfirming] = useState(false);

  const problems = scoringProblems(scoring);
  const whole = scaleProblem(problems);
  const lowers = bandLowerBounds(scoring.bands);
  const choices = passableChoices(scoring);
  /** Has anybody typed in this section? What decides whether removal confirms. */
  const written =
    scoring.instruction.trim() !== '' || scoring.bands.some((band) => band.text.trim() !== '');

  const withBand = (index: number, next: { upper: number | null; text: string }) =>
    onScoring({
      ...scoring,
      bands: scoring.bands.map((band, at) => (at === index ? next : band)),
    });

  return (
    <>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 space-y-1.5">
          <TextArea
            label={
              <>
                Consigne de notation <span className="text-gray-600">(facultative)</span>
              </>
            }
            data-testid="critere-consigne"
            value={scoring.instruction}
            readOnly={readOnly}
            rows={4}
            maxLength={CRITERION_SCORING_INSTRUCTION_MAX}
            placeholder="Comment graduer : ce qu’on regarde, ce qui fait monter la note."
            onChange={(event) => onScoring({ ...scoring, instruction: event.currentTarget.value })}
          />
          <p className="text-right text-caption text-gray-500 leading-4">
            {scoring.instruction.length} / {CRITERION_SCORING_INSTRUCTION_MAX} caractères
          </p>
        </div>
        <Gutter />
      </div>

      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 space-y-2 rounded-md border-2 border-gray-100 bg-gray-50 px-4 pt-3 pb-2.5">
          <p className="font-semibold text-caption text-gray-700">Aperçu de l’échelle</p>
          <ScaleRail scoring={scoring} />
        </div>
        <Gutter />
      </div>

      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="font-semibold text-caption text-gray-700">Tranches</p>
          <p id={bandsHintId} className="text-caption text-gray-600 leading-4">
            Une borne haute et une description par tranche. La borne basse suit la tranche
            précédente, la dernière finit à {CRITERION_SCORING_TOP}. La tranche plancher est celle à
            partir de laquelle le critère est rempli.
          </p>
        </div>
        <Gutter />
      </div>

      {scoring.bands.map((band, index) => {
        const problem = bandProblem(problems, index);
        // The yellow border says « this row is why the button is grey ». The
        // bound gets its own when the bound is what is wrong; a missing
        // description is a defect of the text, and painting the number field
        // for it would send the eye to the one part that is correct.
        const flagged = problem !== null;
        const boundFlagged = problem !== null && problem.kind !== 'empty-text';
        const last = index === scoring.bands.length - 1;
        const lower = lowers[index];
        const number = index + 1;
        return (
          <div
            // The INDEX as the key, for the reason the examples give: the list
            // is positional, and keying on a value would remount the field
            // under the caret on every keystroke.
            // biome-ignore lint/suspicious/noArrayIndexKey: positional list, see above
            key={index}
            className="flex items-start gap-2"
          >
            <div className="min-w-0 flex-1 space-y-1">
              <div
                data-testid="critere-tranche"
                className={`flex items-stretch overflow-hidden rounded-md border-2 bg-white ${
                  flagged ? 'border-jaune-deep' : 'border-gray-300 focus-within:border-bleu-nuit'
                }`}
              >
                <div className="flex w-29 shrink-0 items-center justify-center gap-1.5 border-gray-300 border-r-2 bg-gray-50 px-2 py-2.5">
                  {/*
                    The lower bound is DERIVED and never typed (§6), so it is a
                    span and not a disabled field: a control nobody can use is a
                    control an operator keeps trying. « ? » is the honest answer
                    when the band above has no end yet.
                  */}
                  <span className="font-semibold text-gray-600 text-small tabular-nums">
                    {lower ?? '?'}
                  </span>
                  <span aria-hidden="true" className="text-gray-400 text-small">
                    -
                  </span>
                  {last ? (
                    <span className="w-12 text-center font-semibold text-gray-600 text-small tabular-nums">
                      {CRITERION_SCORING_TOP}
                    </span>
                  ) : (
                    <input
                      type="text"
                      inputMode="numeric"
                      data-testid="critere-tranche-borne"
                      aria-label={`Borne haute de la tranche ${number}`}
                      aria-invalid={boundFlagged}
                      aria-describedby={problem === null ? undefined : `${captionId}-${index}`}
                      value={band.upper ?? ''}
                      readOnly={readOnly}
                      maxLength={3}
                      onChange={(event) =>
                        withBand(index, {
                          upper: bandUpperFromInput(event.currentTarget.value, band.upper),
                          text: band.text,
                        })
                      }
                      className={`h-8 w-12 rounded-sm border-2 px-2 text-right font-semibold text-small tabular-nums read-only:bg-gray-50 read-only:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit ${
                        boundFlagged
                          ? 'border-jaune-deep bg-jaune-tint'
                          : 'border-gray-300 bg-white focus-visible:border-bleu-nuit'
                      }`}
                    />
                  )}
                </div>
                <textarea
                  data-testid="critere-tranche-texte"
                  aria-label={`Description de la tranche ${number}`}
                  aria-describedby={bandsHintId}
                  value={band.text}
                  readOnly={readOnly}
                  rows={3}
                  maxLength={CRITERION_BAND_TEXT_MAX}
                  placeholder="Ce que cette tranche couvre, dans les mots du critère."
                  onChange={(event) =>
                    withBand(index, { upper: band.upper, text: event.currentTarget.value })
                  }
                  /*
                    `field-sizing-content` and a `rows` floor: a description
                    runs to a thousand characters (H44) and a scale is READ as
                    a whole - five boxes each showing three of their six lines
                    would be five sentences cut mid-word. The attribute is the
                    fallback where the property is not supported, so the field
                    is never shorter than what it was before.
                  */
                  className="field-sizing-content min-w-0 flex-1 resize-y px-3 py-2.5 text-small leading-relaxed read-only:bg-gray-50 read-only:text-gray-700 focus-visible:outline-none"
                />
                <div
                  className={`flex w-26 shrink-0 flex-col items-center justify-center gap-1 border-gray-300 border-l-2 px-2 py-2.5 ${
                    index === scoring.plancher ? 'bg-bleu-nuit-tint' : 'bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name={plancherGroup}
                    data-testid="critere-tranche-plancher"
                    aria-label={`Plancher sur la tranche ${number}`}
                    checked={index === scoring.plancher}
                    // `disabled` and not `readOnly`, the one exception of this
                    // file: a browser ignores `readOnly` on a radio, so the
                    // « Voir » of a version would still be clickable.
                    disabled={readOnly}
                    onChange={() => onScoring(withPlancher(scoring, index))}
                    className="size-4.5 accent-bleu-nuit"
                  />
                  <span
                    className={`text-caption leading-4 ${
                      index === scoring.plancher ? 'font-semibold text-bleu-nuit' : 'text-gray-500'
                    }`}
                  >
                    plancher
                  </span>
                </div>
              </div>
              {problem === null ? null : (
                <p
                  id={`${captionId}-${index}`}
                  role="alert"
                  className="flex items-center gap-1.5 text-caption text-jaune-deep leading-4"
                >
                  <span className="inline-flex shrink-0">
                    <Icon name="warning" size={14} />
                  </span>
                  {bandProblemSentence(problem, scoring.bands.length)}
                </p>
              )}
            </div>
            {readOnly ? (
              <Gutter />
            ) : (
              <>
                {/* A band does not duplicate (§4.2): its copy slot stays empty. */}
                <GutterSlot />
                <BandRemoveReason closed={!canRemoveBand(scoring)}>
                  <TooltipShield closed={!canRemoveBand(scoring)} layout="mt-1 shrink-0">
                    <button
                      type="button"
                      data-testid="critere-tranche-retirer"
                      aria-label={`Retirer la tranche ${number}`}
                      // Closed at two bands rather than hidden: the floor is H44's,
                      // and a cross that vanishes on the second-to-last row reads as
                      // a bug rather than as a rule.
                      disabled={!canRemoveBand(scoring)}
                      onClick={() => onScoring(removeBand(scoring, index))}
                      className="inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-rouge-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent disabled:hover:text-gray-300"
                    >
                      <Icon name="close" size={18} />
                    </button>
                  </TooltipShield>
                </BandRemoveReason>
              </>
            )}
          </div>
        );
      })}

      {/*
        The scale's own defect, under the rows: neither kind names a band, so
        neither can be captioned on one. Unreachable from this editor and
        rendered anyway - a scoring posted by the assistant of §20 can carry
        one, and a grey button with no sentence is the failure the pure module
        exists to prevent.
      */}
      {whole === null ? null : (
        <div className="flex items-start gap-2">
          <p
            role="alert"
            className="flex min-w-0 flex-1 items-center gap-1.5 text-caption text-jaune-deep leading-4"
          >
            <span className="inline-flex shrink-0">
              <Icon name="warning" size={14} />
            </span>
            {scaleProblemSentence(whole)}
          </p>
          <Gutter />
        </div>
      )}

      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-3">
          {readOnly ? (
            <span />
          ) : (
            <button
              type="button"
              data-testid="critere-ajouter-tranche"
              disabled={!canAddBand(scoring)}
              onClick={() => onScoring(addBand(scoring))}
              className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md px-4 font-semibold text-bleu-nuit text-small hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:text-gray-500 disabled:hover:bg-transparent"
            >
              <Icon name="add" size={16} />
              <span>Ajouter une tranche</span>
            </button>
          )}
          <p data-testid="critere-compte-tranches" className="text-caption text-gray-500 leading-4">
            {scoring.bands.length} / {CRITERION_SCORING_BANDS_MAX} tranches
          </p>
        </div>
        <Gutter />
      </div>

      {/*
        « Passable » (#110): a band of the scale, like the plancher, but for the
        whole scale rather than per row - a second radio column would put two
        radios on every band for a choice that is ONE band or none. Only the
        bands below the plancher are offered, so the select cannot write the
        one refusal it would cause; a plancher on the first band leaves none,
        and then there is nothing to choose and no control to show.
      */}
      {choices.length === 0 ? null : (
        <div className="flex items-start gap-2">
          <div data-testid="critere-passable" className="min-w-0 flex-1">
            <Select
              label="Passable à partir de"
              compact
              // `disabled` and not `readOnly`, for the reason the plancher radios
              // give: a select has no read-only state.
              disabled={readOnly}
              value={toSelectValue(scoring.passable === null ? '' : String(scoring.passable))}
              onValueChange={(value) => {
                const raw = fromSelectValue(value);
                onScoring(withPassable(scoring, raw === '' ? null : Number(raw)));
              }}
              options={[
                { value: EMPTY_SELECT_VALUE, label: 'aucune tranche' },
                ...choices.map((choice) => ({
                  value: String(choice.index),
                  label: `${choice.lower ?? '?'} (tranche ${choice.index + 1})`,
                })),
              ]}
              help="Facultatif. Dans les sessions, une note restée sous son seuil se lit en jaune à partir de cette tranche : passable, pas encore rempli. En dessous, en rouge."
            />
          </div>
          <Gutter />
        </div>
      )}

      {readOnly ? null : (
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1 pl-4">
            {confirming ? (
              /*
                Inline and not a modal, the régime of the two confirmations of
                Réglages > Personas: it answers for the block it sits under, and
                a veil over the whole editor would hide the very text it is
                asking about.
              */
              <div
                role="alertdialog"
                aria-label="Retirer la notation"
                className="space-y-2 rounded-md border-2 border-gray-200 bg-gray-50 p-3"
              >
                <p className="text-gray-800 text-small leading-5">
                  La consigne et les {scoring.bands.length} tranches seront effacées. Le critère
                  redeviendra binaire : le scribe rendra un verdict.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    data-testid="critere-notation-retirer-confirmer"
                    onClick={() => {
                      setConfirming(false);
                      onScoring(null);
                    }}
                    className="inline-flex h-9 cursor-pointer items-center rounded-md border-2 border-rouge px-4 font-semibold text-rouge text-small hover:bg-rouge-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
                  >
                    Retirer la notation
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    className="inline-flex h-9 cursor-pointer items-center rounded-md border-2 border-gray-300 px-4 font-semibold text-gray-700 text-small hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                data-testid="critere-notation-retirer"
                // Confirmed only when something was typed (§6): a scale opened
                // by mistake three seconds ago is not work, and a box in front
                // of it would be a question about nothing.
                onClick={() => (written ? setConfirming(true) : onScoring(null))}
                className={QUIET_LINK}
              >
                Retirer la notation
              </button>
            )}
          </div>
          <Gutter />
        </div>
      )}
    </>
  );
}

/**
 * The rail of « Aperçu de l'échelle »: one segment per band, WIDE AS ITS BAND.
 *
 * The only inline styles of this screen, and they are the ones the maquette is
 * about: the widths ARE the numbers somebody typed, so a rail of equal segments
 * would be a drawing of a different scale. The régime of `jump-popover.tsx` -
 * measured geometry goes inline, everything else is a token. Colour stays in
 * tokens even inside the hatch, which is why it names `var(--color-*)` rather
 * than the two hexes the maquette spells.
 */
function ScaleRail({ scoring }: { scoring: CriterionScoringDraft }) {
  const { segments, holes, boundaries } = scalePreview(scoring);
  const plancher = boundaries.find((boundary) => boundary.isPlancher) ?? null;
  const passable = boundaries.find((boundary) => boundary.isPassable) ?? null;

  return (
    <>
      <div className="relative h-3 overflow-hidden rounded-sm bg-gray-100">
        {segments.map((segment) => (
          <span
            key={`s-${segment.left}`}
            style={{ left: `${segment.left}%`, width: `${segment.width}%` }}
            className={`absolute inset-y-0 border-white border-r-2 ${
              segment.filled ? 'bg-bleu-nuit' : segment.passable ? 'bg-jaune' : 'bg-rail'
            }`}
          />
        ))}
        {holes.map((hole) => (
          <span
            key={`h-${hole.left}`}
            aria-hidden="true"
            style={{
              left: `${hole.left}%`,
              width: `${hole.width}%`,
              backgroundImage:
                'repeating-linear-gradient(135deg, var(--color-jaune-tint) 0 5px, var(--color-jaune-deep) 5px 7px)',
            }}
            className="absolute inset-y-0 border-white border-r-2"
          />
        ))}
      </div>

      <div className="relative h-8">
        {boundaries.map((boundary, index) => (
          <span
            key={boundary.value}
            style={{ left: `${boundary.value}%` }}
            className={`absolute top-0 text-caption tabular-nums leading-4 ${
              boundary.isPlancher
                ? 'font-bold text-bleu-nuit'
                : boundary.isPassable
                  ? 'font-bold text-jaune-deep'
                  : 'text-gray-600'
            } ${
              index === 0
                ? ''
                : index === boundaries.length - 1
                  ? '-translate-x-full'
                  : '-translate-x-1/2'
            }`}
          >
            {boundary.value}
          </span>
        ))}
        {plancher === null ? null : (
          <span
            style={{ left: `${plancher.value}%` }}
            className="-translate-x-1/2 absolute top-4 flex flex-col items-center gap-0.5 whitespace-nowrap font-semibold text-bleu-nuit text-caption leading-4"
          >
            <svg viewBox="0 0 8 8" aria-hidden="true" className="size-2 fill-bleu-nuit">
              <title>plancher</title>
              <path d="M4 0 8 8H0z" />
            </svg>
            plancher
          </span>
        )}
        {passable === null ? null : (
          <span
            style={{ left: `${passable.value}%` }}
            className="-translate-x-1/2 absolute top-4 flex flex-col items-center gap-0.5 whitespace-nowrap font-semibold text-caption text-jaune-deep leading-4"
          >
            <svg viewBox="0 0 8 8" aria-hidden="true" className="size-2 fill-jaune-deep">
              <title>passable</title>
              <path d="M4 0 8 8H0z" />
            </svg>
            passable
          </span>
        )}
      </div>

      <p className="text-caption text-gray-600 leading-4">
        {plancher === null
          ? 'Le plancher se lira dès que les bornes au-dessous de lui seront posées.'
          : `En bleu nuit, les tranches où le critère est rempli : à partir du plancher, ${plancher.value}.`}
        {passable === null
          ? ''
          : ` En jaune, les tranches passables : à partir de ${passable.value}.`}
        {holes.length === 0
          ? ''
          : ` ${holes.length === 1 ? 'La zone hachurée n’a pas de bornes' : 'Les zones hachurées n’ont pas de bornes'} : une tranche sans fin laisse la suivante sans début.`}
      </p>
    </>
  );
}

export function CriterionEditorFields({
  draft,
  readOnly,
  nameError,
  onName,
  onNameCommit,
  onDirection,
  onExample,
  onAddExample,
  onRemoveExample,
  onDuplicateExample,
  onScoring,
}: CriterionEditorFieldsProps) {
  /*
    Read ONCE above the loop, like the three other screens that carry this
    gesture: it is a property of the LIST, so computing it per row would be
    thirty answers to one question.
  */
  const examplesBlocked = exampleDuplicationBlocked(draft.examples);

  return (
    <div className="space-y-7">
      <section className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          {/* The hint or the refusal, never neither: a field whose rule is only
              painted beside it has no rule for whoever cannot see it. The package
              field points its describedby at whichever it shows. */}
          <TextField
            label="Nom"
            data-testid="critere-nom"
            value={draft.name}
            readOnly={readOnly}
            required
            aria-required="true"
            maxLength={CRITERION_NAME_MAX}
            placeholder="Un nom unique parmi les critères actifs"
            onChange={(event) => onName(event.currentTarget.value)}
            onBlur={onNameCommit}
            help="Unique parmi les critères actifs. Renommer ne crée pas de version."
            status={nameError === null ? 'default' : 'error'}
            message={nameError ?? undefined}
          />
        </div>
        <Gutter />
      </section>

      <section className="space-y-2">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1 space-y-1.5">
            {/* The package field draws its help UNDER the field: the hint that sat
                between the label and the field now follows it. */}
            <TextArea
              label="Direction d’IA"
              data-testid="critere-direction"
              value={draft.direction}
              readOnly={readOnly}
              required
              aria-required="true"
              help="Comment le scribe doit juger. Servie telle quelle, avec les exemples, à chaque tour."
              rows={7}
              maxLength={CRITERION_DIRECTION_MAX}
              placeholder="Ce que la personne doit avoir dit pour que le critère soit satisfait, et ce qui ne suffit pas."
              onChange={(event) => onDirection(event.currentTarget.value)}
            />
            {/* The counter is a bound of INPUT and not of meaning (H10): an
                operator meets it by mistake, never by intent, so it sits under
                the field in grey rather than warning about anything. */}
            <p className="text-right text-caption text-gray-500 leading-4">
              {draft.direction.length} / {CRITERION_DIRECTION_MAX} caractères
            </p>
          </div>
          <Gutter />
        </div>
      </section>

      {/*
        A `<fieldset>` and not a `<section>`, because the examples are a GROUP
        of fields sharing one label - and a `<legend>` is the only way to say so
        without a `role="group"` the a11y lint refuses by naming this element
        instead. It also puts the group's name in front of « Exemple 3 » for a
        screen reader, which is what tells a list of numbered fields from three
        unrelated ones. The browser's own `min-inline-size: min-content` on a
        fieldset is reset once, in `styles.css`, for the reason BUILD-916 gives.
      */}
      <fieldset className="min-w-0 space-y-2.5 border-0 p-0">
        {/*
          The `<legend>` is the FIRST child of the fieldset, and that is not a
          layout choice: a fieldset's content model is « optionally a legend,
          followed by flow content », so a legend wrapped in a `<div>` still
          renders but stops being the group's name - the one thing it was put
          here to be. Hence the margin rather than a shared `space-y` wrapper.
        */}
        <legend className="mb-1 block font-semibold text-gray-800 text-small">
          Exemples positifs <span className="font-normal text-gray-600">(facultatifs)</span>
        </legend>
        <p className="text-caption text-gray-600 leading-4">
          Des extraits de réponse qui satisfont le critère, sans étiquette ni commentaire. Un
          critère sans exemple est jugé sur sa seule direction.
        </p>

        {draft.examples.map((example, index) => (
          <div
            // The INDEX as the key, and here it is the right one: the list is
            // positional - « l'exemple 2 » is the second field, whatever it
            // holds - and the values are not identities. Keying on the text
            // would remount a field on every keystroke and lose the caret.
            // biome-ignore lint/suspicious/noArrayIndexKey: positional list, see above
            key={index}
            className="flex items-start gap-2"
          >
            {/* Kept native, with the number INSIDE its frame (§6, see the head): the
                package text area takes no leading cell. */}
            <div className="flex min-w-0 flex-1 items-stretch overflow-hidden rounded-md border-2 border-gray-300 bg-white focus-within:border-bleu-nuit">
              <span
                aria-hidden="true"
                className="flex w-10 shrink-0 items-center justify-center border-gray-300 border-r-2 bg-gray-50 font-semibold text-gray-600 text-small"
              >
                {index + 1}
              </span>
              <textarea
                data-testid="critere-exemple"
                aria-label={`Exemple ${index + 1}`}
                value={example}
                readOnly={readOnly}
                rows={2}
                maxLength={CRITERION_EXAMPLE_MAX}
                placeholder="Un extrait de réponse qui satisfait le critère"
                onChange={(event) => onExample(index, event.currentTarget.value)}
                className="min-w-0 flex-1 resize-y px-3 py-2.5 text-small leading-relaxed read-only:bg-gray-50 read-only:text-gray-700 focus-visible:outline-none"
              />
            </div>
            {/* The gutter carries two gestures since #117: the copy, THEN the
                cross (§4.2). Both go together in read-only, and the column then
                keeps its width, which is the width of the two. */}
            {readOnly ? (
              <Gutter />
            ) : (
              <>
                <DuplicateButton
                  testId="critere-exemple-dupliquer"
                  label={`Dupliquer l’exemple ${index + 1}`}
                  blocked={examplesBlocked}
                  className="mt-1"
                  onClick={() => onDuplicateExample(index)}
                />
                <button
                  type="button"
                  data-testid="critere-exemple-retirer"
                  aria-label={`Retirer l’exemple ${index + 1}`}
                  onClick={() => onRemoveExample(index)}
                  className="mt-1 inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-rouge-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
                >
                  <Icon name="close" size={18} />
                </button>
              </>
            )}
          </div>
        ))}

        <div className="flex items-start gap-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-3">
            {readOnly ? (
              <span />
            ) : (
              <button
                type="button"
                data-testid="critere-ajouter-exemple"
                disabled={!canAddExample(draft)}
                onClick={onAddExample}
                className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md px-4 font-semibold text-bleu-nuit text-small hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:text-gray-500 disabled:hover:bg-transparent"
              >
                <Icon name="add" size={16} />
                <span>Ajouter un exemple</span>
              </button>
            )}
            <p
              data-testid="critere-compte-exemples"
              className="text-caption text-gray-500 leading-4"
            >
              {draft.examples.length} / {CRITERION_EXAMPLES_MAX} exemples
            </p>
          </div>
          <Gutter />
        </div>
      </fieldset>

      {/*
        UNDER the examples, at the same width and with the same gutter (§6,
        maquette `Main.dc.html`). A fieldset for the reason the examples are one:
        it is a group of fields under one name, and a `<legend>` is what says so
        without a `role="group"` the a11y lint refuses.
      */}
      <fieldset className="min-w-0 space-y-2.5 border-0 p-0">
        <legend className="mb-1 block font-semibold text-gray-800 text-small">
          Notation <span className="font-normal text-gray-600">(facultative)</span>
        </legend>
        <p className="text-caption text-gray-600 leading-4">
          Sans notation, le scribe rend un verdict : rempli ou non. Avec, il rend une note de 0 à{' '}
          {CRITERION_SCORING_TOP} guidée par les tranches, et le critère est rempli à partir du
          plancher, sauf seuil posé par l’objectif qui le cite.
        </p>

        {draft.scoring === null ? (
          <div className="flex items-start gap-2">
            <div className="flex min-w-0 flex-1 items-center">
              {readOnly ? null : (
                <button
                  type="button"
                  data-testid="critere-notation-ajouter"
                  onClick={() => onScoring(emptyScoring())}
                  className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md px-4 font-semibold text-bleu-nuit text-small hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
                >
                  <Icon name="add" size={16} />
                  <span>Ajouter une notation</span>
                </button>
              )}
            </div>
            <Gutter />
          </div>
        ) : (
          <ScoringFields scoring={draft.scoring} readOnly={readOnly} onScoring={onScoring} />
        )}
      </fieldset>
    </div>
  );
}
